import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Camera, RotateCcw, Check, X, Loader2 } from "lucide-react";

const WebcamCapture = ({ onCapture, onClear, capturedPhoto }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const streamRef = useRef(null);

  // Attach stream to video element when ref becomes available
  useEffect(() => {
    if (streaming && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [streaming]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = useCallback(async () => {
    setError(null);
    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 480 }, height: { ideal: 360 } }
      });
      streamRef.current = stream;
      setStreaming(true);
      setStarting(false);
    } catch (err) {
      setStarting(false);
      if (err.name === "NotAllowedError") {
        setError("Camera permission denied. Please allow camera access in your browser settings.");
      } else if (err.name === "NotFoundError") {
        setError("No camera found on this device.");
      } else if (err.name === "NotReadableError") {
        setError("Camera is in use by another application.");
      } else {
        setError("Could not access camera. Ensure you are using HTTPS and camera is available.");
      }
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setStreaming(false);
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth || 480;
    canvas.height = video.videoHeight || 360;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
    onCapture(dataUrl);
    stopCamera();
  }, [onCapture, stopCamera]);

  const retake = useCallback(() => {
    onClear();
    startCamera();
  }, [onClear, startCamera]);

  return (
    <Card className="border-slate-200" data-testid="webcam-capture">
      <CardContent className="pt-4 pb-4">
        <p className="text-sm font-medium mb-2 flex items-center gap-2">
          <Camera className="h-4 w-4" /> Student Photo (Webcam) <span className="text-red-500">*</span>
        </p>

        {capturedPhoto ? (
          <div className="space-y-2">
            <div className="relative w-32 h-32 mx-auto rounded-lg overflow-hidden border-2 border-emerald-300">
              <img src={capturedPhoto} alt="Captured" className="w-full h-full object-cover" data-testid="captured-photo" />
              <div className="absolute top-1 right-1">
                <Check className="h-4 w-4 text-emerald-600 bg-white rounded-full p-0.5" />
              </div>
            </div>
            <div className="flex justify-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={retake} data-testid="retake-photo-btn">
                <RotateCcw className="h-3 w-3 mr-1" /> Retake
              </Button>
              <Button type="button" variant="ghost" size="sm" className="text-red-600" onClick={() => { onClear(); stopCamera(); }} data-testid="remove-photo-btn">
                <X className="h-3 w-3 mr-1" /> Remove
              </Button>
            </div>
          </div>
        ) : streaming ? (
          <div className="space-y-2">
            <div className="relative w-48 h-36 mx-auto rounded-lg overflow-hidden border-2 border-blue-300 bg-black">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="webcam-video" />
            </div>
            <div className="flex justify-center gap-2">
              <Button type="button" size="sm" onClick={capturePhoto} data-testid="capture-photo-btn">
                <Camera className="h-3 w-3 mr-1" /> Capture
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={stopCamera}>
                <X className="h-3 w-3 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center space-y-2">
            {error && <p className="text-xs text-red-500 max-w-xs mx-auto">{error}</p>}
            <Button type="button" variant="outline" size="sm" onClick={startCamera} disabled={starting} data-testid="open-camera-btn">
              {starting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Camera className="h-3 w-3 mr-1" />}
              {starting ? "Starting Camera..." : "Open Camera"}
            </Button>
            <p className="text-[10px] text-slate-400">Allow camera permission when prompted</p>
          </div>
        )}

        <canvas ref={canvasRef} className="hidden" />
      </CardContent>
    </Card>
  );
};

export default WebcamCapture;
